/**
 * Painel de Frota — vinculação de equipamentos ao cliente.
 * Inserido em /app/clientes/:id (Central 360° do cliente).
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Truck, Plus, Loader2, Search, X, Link2Off, Wrench, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_CFG = {
  locado:         { label: "LOCADO",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  reservado:      { label: "RESERVADO",    cls: "bg-blue-100 text-blue-800 border-blue-300" },
  na_oficina:     { label: "NA OFICINA",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  em_atendimento: { label: "ATENDIMENTO",  cls: "bg-orange-100 text-orange-800 border-orange-300" },
  disponivel:     { label: "DISPONÍVEL",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  extraviado:     { label: "EXTRAVIADO",   cls: "bg-red-100 text-red-700 border-red-300" },
};

export default function ClienteFrotaPanel({ clienteId }) {
  const { user } = useAuth();
  const podeEditar = ["diretor", "gerente", "supervisao", "operador", "oficina"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVincular, setModalVincular] = useState(false);
  const [confirmDesvincular, setConfirmDesvincular] = useState(null);

  const load = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/frota`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Erro ao carregar frota");
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { load(); }, [load]);

  const desvincular = async () => {
    if (!confirmDesvincular) return;
    try {
      await api.post(`/clientes/${clienteId}/frota/${confirmDesvincular.id}/desvincular`);
      toast.success(`Equipamento ${confirmDesvincular.serial} desvinculado`);
      setConfirmDesvincular(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <Card className="p-4" data-testid="painel-frota-cliente">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2 text-slate-900">
            <Truck className="w-4 h-4 text-orange-600" />
            Frota do Cliente
          </h3>
          <p className="text-[10px] text-slate-500">Equipamentos vinculados — {items.length} ativos</p>
        </div>
        {podeEditar && (
          <Button size="sm" onClick={() => setModalVincular(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-vincular-equipamento">
            <Plus className="w-3.5 h-3.5 mr-1" /> Associar equipamento
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-500">
          Nenhum equipamento vinculado a este cliente.
          {podeEditar && <div className="mt-1">Clique em <strong>Associar equipamento</strong> para iniciar.</div>}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Serial</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Modelo</TableHead>
              <TableHead>Status</TableHead>
              {podeEditar && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((eq) => {
              const st = STATUS_CFG[eq.status] || { label: eq.status, cls: "bg-slate-100" };
              return (
                <TableRow key={eq.id} data-testid={`frota-row-${eq.id}`}>
                  <TableCell className="font-mono text-xs">{eq.serial}</TableCell>
                  <TableCell className="font-mono text-xs">{eq.placa || "—"}</TableCell>
                  <TableCell className="text-xs">{eq.modelo || "—"}</TableCell>
                  <TableCell>
                    <Badge className={`${st.cls} border text-[10px] font-mono`}>{st.label}</Badge>
                  </TableCell>
                  {podeEditar && (
                    <TableCell>
                      <Button size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => setConfirmDesvincular(eq)}
                        data-testid={`btn-desvincular-${eq.id}`}
                        title="Desvincular">
                        <Link2Off className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <VincularModal
        open={modalVincular}
        onOpenChange={setModalVincular}
        clienteId={clienteId}
        onVinculado={load}
      />

      <Dialog open={!!confirmDesvincular} onOpenChange={(o) => !o && setConfirmDesvincular(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desvincular equipamento?</DialogTitle>
            <DialogDescription>
              {confirmDesvincular && (
                <>Equipamento <strong>{confirmDesvincular.serial}</strong> ({confirmDesvincular.placa || "—"})
                  voltará a ficar disponível para outros clientes.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDesvincular(null)}>Cancelar</Button>
            <Button onClick={desvincular} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-desvincular">Desvincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function VincularModal({ open, onOpenChange, clienteId, onVinculado }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/frota/disponiveis", { params: { q, limit: 50 } });
        setItems(data.items || []);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  const vincular = async (eq) => {
    setSaving(true);
    try {
      await api.post(`/clientes/${clienteId}/frota/vincular`, { equipamento_id: String(eq.id) });
      toast.success(`Equipamento ${eq.serial} vinculado`);
      onVinculado && onVinculado();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao vincular");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="vincular-modal">
        <DialogHeader>
          <DialogTitle>Associar equipamento ao cliente</DialogTitle>
          <DialogDescription>
            Busque por serial ou placa. Equipamentos em manutenção aparecem em cinza e não podem ser vinculados.
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input className="pl-8" placeholder="Serial ou placa..."
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="vincular-busca" />
        </div>

        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-500">
            Nenhum equipamento disponível encontrado.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serial</TableHead>
                  <TableHead>Placa</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((eq) => {
                  const bloqueado = eq.pode_vincular === false;
                  const tooltip = bloqueado
                    ? (eq.motivo_bloqueio === "aguardando_pecas"
                        ? "Equipamento aguardando peças — atualize na Oficina antes"
                        : "Equipamento em manutenção — marque como disponível na Oficina antes")
                    : "";
                  const badgeCls = bloqueado
                    ? (eq.motivo_bloqueio === "aguardando_pecas"
                        ? "bg-red-100 text-red-700 border-red-300"
                        : "bg-amber-100 text-amber-800 border-amber-300")
                    : (eq.status === "na_oficina"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-slate-100 text-slate-700 border-slate-300");
                  const badgeLabel = eq.sub_status_label || (eq.status === "disponivel" ? "Disponível" : eq.status);
                  const IconBadge = bloqueado
                    ? (eq.motivo_bloqueio === "aguardando_pecas" ? Clock : Wrench)
                    : null;
                  return (
                    <TableRow
                      key={eq.id}
                      className={bloqueado ? "opacity-60" : ""}
                      data-testid={`row-vincular-${eq.id}`}
                    >
                      <TableCell className="font-mono text-xs">{eq.serial}</TableCell>
                      <TableCell className="font-mono text-xs">{eq.placa || "—"}</TableCell>
                      <TableCell>
                        <Badge className={`${badgeCls} border text-[10px] font-mono inline-flex items-center gap-1`}>
                          {IconBadge && <IconBadge className="w-3 h-3" />}
                          {badgeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => vincular(eq)}
                          disabled={saving || bloqueado}
                          title={tooltip}
                          className={bloqueado
                            ? "h-7 text-xs bg-slate-200 text-slate-500 cursor-not-allowed"
                            : "bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs"}
                          data-testid={`btn-vincular-eq-${eq.id}`}
                        >
                          {bloqueado ? "—" : "Vincular"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
